import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { ShopifyStorefrontClient } from "@redotech/shopify-storefront";
import { ReactNode, createContext, memo, useContext, useEffect } from "react";
import { PermissionsContext } from "./auth/permissions-context";
import { ReloadUserContext, UserContext, UserLoadStatusContext } from "./user";

export interface OnboardingData {
  averageWeight?: number;
}

// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export {
  ReloadTeamContext,
  TeamContext,
} from "@redotech/redo-web/context/team-context";

export const StorefrontClientContext = createContext<
  ShopifyStorefrontClient | undefined
>(undefined);

export const TeamAndUserRequired = memo(function TeamAndUserRequired({
  children,
}: {
  children: ReactNode | ReactNode[];
}) {
  const team = useContext(TeamContext);
  const user = useContext(UserContext);
  const userLoadStatus = useContext(UserLoadStatusContext);
  const reloadUser = useContext(ReloadUserContext);
  const permissions = useContext(PermissionsContext);

  useEffect(() => {
    const timeoutId = setTimeout(
      () => {
        if (!team || !user || !permissions) {
          console.error(
            `[DEBUG] Still rendering null. Team: ${!!team}, User: ${!!user}, Permissions: ${!!permissions}`,
          );
        }
      },
      Temporal.Duration.from({ seconds: 10 }).total("milliseconds"),
    );
    return () => clearTimeout(timeoutId);
  }, [team, user, permissions]);

  if (team && !user && userLoadStatus.failed) {
    return (
      <Flex
        align="center"
        dir="column"
        gap="xl"
        justify="center"
        minH="screen"
        p="2xl"
        w="full"
      >
        <Text fontSize="xl" fontWeight="semibold">
          We couldn&apos;t load your profile
        </Text>
        <Text textAlign="center" textColor="secondary">
          Please try loading your profile again.
        </Text>
        <RedoButton
          disabled={!reloadUser || userLoadStatus.pending}
          hierarchy="primary"
          onClick={() => reloadUser?.()}
          pending={userLoadStatus.pending}
          size="lg"
          text="Try again"
        />
      </Flex>
    );
  }
  if (!team || !user || !permissions) {
    return null;
  } else {
    return children;
  }
});

export const OnboardingAverageProductWeightContext = createContext<
  OnboardingData | undefined
>(undefined);
